// Smart Create panel — uniform person-card payload mapper (marker cp-create-cards-v1).
//
// The "Create New" slide-over renders every contact person as a UNIFORM CARD:
// card 0 is the primary contact (was the standalone Contact Name + top-level
// Email/Phone rows), cards 1..N are the added people. Each card carries a full
// name, N email rows, and N phone rows (type/number/ext). Exactly ONE email and
// ONE phone across ALL cards may be primary for the record — they become the
// client's preferred_email / preferred_phone; the default is card 0's first
// entries and clicking a Primary chip moves primacy.
//
// This module is the single source of truth for turning those cards into the
// create-endpoint payload:
//   - clientType + recordName (org name wins; else card 0's full name — a
//     composed couple display like "Alan & Karen Dorman" passes through verbatim)
//   - primaryEmail / primaryPhone (the primary-flagged values, defaulting to
//     card 0's first non-empty entries)
//   - persons[] entries for POST /api/crm/clients:
//       { full_name, role_label?, emails: [{ value, primary? }],
//         phones: [{ value, type?, ext?, primary? }] }
//     Card 0 is included ONLY when 2+ named cards exist (a single-card create
//     stays byte-identical to the historical single-contact create: no
//     contact_persons row is materialized for the contact itself).
//
// BACKWARD COMPATIBLE inputs: the legacy added-person shape ({ name, email,
// phone } single strings) and bare-string email rows are accepted and
// normalized, so old drafts/callers keep working.
//
// Pure: no DOM, no React, no I/O — safe under the vitest `node` env that gates
// `npm test` (CLAUDE.md critical rule 12).

// Minification-proof shipped-bundle marker. Referenced by the cards container
// (data attribute) in src/App.tsx so it can never be tree-shaken out.
export const CREATE_CARDS_MARKER = 'cp-create-cards-v1'

// Movable MAIN card marker (train 6). The card-header badge reads "Main"
// (amber), renders only when 2+ person cards exist, and can be MOVED to any
// card; is_primary, the person-type record display name, and the DEFAULT
// primary email/phone (chips untouched) all follow the main card. Referenced
// next to data-cp-cards and on the badge (data-cp-main) in src/App.tsx.
export const CREATE_MAIN_MARKER = 'cp-create-main-v1'

/** One email row on a card. Bare strings (legacy) are accepted too. */
export interface CreateCardEmailRow {
  value?: string
  /** legacy alias used by some API surfaces */
  address?: string
  primary?: boolean
}

/** One phone row on a card. `number` is the legacy alias for `value`. */
export interface CreateCardPhoneRow {
  value?: string
  number?: string
  type?: string
  /** legacy alias for `type` */
  label?: string
  ext?: string
  primary?: boolean
}

/** A person card as assembled by the panel (card 0 = primary contact). */
export interface CreatePersonCardInput {
  name?: string
  roleLabel?: string
  emails?: (string | CreateCardEmailRow)[]
  phones?: (string | CreateCardPhoneRow)[]
  /** Legacy single-value shape (pre-cards added-person rows). */
  email?: string
  phone?: string
}

/** persons[] entry for POST /api/crm/clients (cp-create-cards-api-v1). */
export interface CreatePersonsApiEntry {
  full_name: string
  role_label?: string
  /** cp-create-main-v1: stamped on the MAIN card's entry so contact_persons.
   *  is_primary follows the movable Main badge (server honors the first
   *  flagged entry; absent flags fall back to first-in-array, back-compat). */
  is_primary?: boolean
  emails?: { value: string; primary?: boolean }[]
  phones?: { value: string; type?: string; ext?: string; primary?: boolean }[]
}

export interface CreatePersonsPayload {
  clientType: 'person' | 'organization'
  /** preferred_name for the record (org name when set, else card 0's name). */
  recordName: string
  /** Client-level preferred_email — the primary-flagged value. */
  primaryEmail: string | null
  /** Client-level preferred_phone — the primary-flagged value. */
  primaryPhone: string | null
  /** True when card 0 rides inside persons[] (2+ named cards). The caller must
   *  then SKIP its separate card-0 extra-row persistence to avoid dupes. */
  card1InPersons: boolean
  persons: CreatePersonsApiEntry[]
}

interface NormalizedEmail { value: string; primary: boolean }
interface NormalizedPhone { value: string; type?: string; ext?: string; primary: boolean }
interface NormalizedCard {
  name: string
  roleLabel?: string
  emails: NormalizedEmail[]
  phones: NormalizedPhone[]
}

function normalizeCard(card: CreatePersonCardInput | null | undefined): NormalizedCard {
  const c = card || {}
  const emails: NormalizedEmail[] = []
  const emailRows: (string | CreateCardEmailRow)[] = Array.isArray(c.emails) ? c.emails : []
  for (const row of emailRows) {
    const value = typeof row === 'string' ? row.trim() : String(row?.value ?? row?.address ?? '').trim()
    if (!value) continue
    emails.push({ value, primary: typeof row === 'object' && !!row && row.primary === true })
  }
  // Legacy single-value shape: { email: 'a@b.com' }
  if (emails.length === 0 && typeof c.email === 'string' && c.email.trim()) {
    emails.push({ value: c.email.trim(), primary: false })
  }
  const phones: NormalizedPhone[] = []
  const phoneRows: (string | CreateCardPhoneRow)[] = Array.isArray(c.phones) ? c.phones : []
  for (const row of phoneRows) {
    const value = typeof row === 'string' ? row.trim() : String(row?.value ?? row?.number ?? '').trim()
    if (!value) continue
    const type = typeof row === 'object' && row ? (row.type ?? row.label) : undefined
    const ext = typeof row === 'object' && row ? row.ext : undefined
    phones.push({
      value,
      type: type ? String(type).trim() || undefined : undefined,
      ext: ext ? String(ext).trim() || undefined : undefined,
      primary: typeof row === 'object' && !!row && row.primary === true,
    })
  }
  // Legacy single-value shape: { phone: '(555) 555-5555' }
  if (phones.length === 0 && typeof c.phone === 'string' && c.phone.trim()) {
    phones.push({ value: c.phone.trim(), primary: false })
  }
  return {
    name: String(c.name ?? '').replace(/\s+/g, ' ').trim(),
    roleLabel: c.roleLabel && String(c.roleLabel).trim() ? String(c.roleLabel).trim() : undefined,
    emails,
    phones,
  }
}

/** Build the create-endpoint payload from the panel's person cards.
 *  `cards[0]` is the first contact card; `opts.orgName` is the (trimmed)
 *  Organization Name when set — it flips the record to org-type.
 *  `opts.mainIndex` (cp-create-main-v1, default 0) names the MAIN card:
 *  contact_persons.is_primary, the person-type record display name, and the
 *  DEFAULT primary email/phone (when no chip was ever clicked) all follow it.
 *  Explicit primary-flagged rows (chip clicks) always win over the default. */
export function buildCreatePersonsPayload(
  cards: (CreatePersonCardInput | null | undefined)[],
  opts?: { orgName?: string; mainIndex?: number },
): CreatePersonsPayload {
  const norm = (Array.isArray(cards) ? cards : []).map(normalizeCard)
  const card0 = norm[0] || { name: '', emails: [], phones: [] }
  const orgName = (opts?.orgName || '').trim()
  // Main card (cp-create-main-v1): clamp to an existing card; out-of-range or
  // unset falls back to card 0 (byte-identical to the pre-mainIndex behavior).
  const rawMain = opts?.mainIndex
  const mainIdx = Number.isInteger(rawMain) && (rawMain as number) > 0 && (rawMain as number) < norm.length
    ? (rawMain as number)
    : 0
  const mainCard = norm[mainIdx] || card0

  // ── Primary email/phone: first primary-flagged non-empty value across all
  //    cards (card order), else the MAIN card's first filled entry (the
  //    shipped default was card 0's — identical when mainIndex is 0).
  let primaryEmail: string | null = null
  for (const card of norm) {
    const flagged = card.emails.find((e) => e.primary)
    if (flagged) { primaryEmail = flagged.value; break }
  }
  if (primaryEmail == null) primaryEmail = mainCard.emails[0]?.value ?? null
  let primaryPhone: string | null = null
  for (const card of norm) {
    const flagged = card.phones.find((p) => p.primary)
    if (flagged) { primaryPhone = flagged.value; break }
  }
  if (primaryPhone == null) primaryPhone = mainCard.phones[0]?.value ?? null

  // ── persons[]: named cards only. Card 0 joins ONLY when 2+ cards are named
  //    (single-card creates stay identical to the historical create).
  const namedIdx = norm.map((c, i) => (c.name ? i : -1)).filter((i) => i >= 0)
  const card1InPersons = namedIdx.includes(0) && namedIdx.length >= 2
  const personIdx = namedIdx.filter((i) => i !== 0 || card1InPersons)

  const persons: CreatePersonsApiEntry[] = personIdx.map((i) => {
    const card = norm[i]
    const entry: CreatePersonsApiEntry = { full_name: card.name }
    // The MAIN card's persons[] entry carries is_primary so the server's
    // contact_persons.is_primary follows the movable Main badge
    // (cp-create-main-v1). Absent on every entry ⇒ server falls back to
    // first-in-array (back-compat).
    if (i === mainIdx) entry.is_primary = true
    if (card.roleLabel) entry.role_label = card.roleLabel
    if (card.emails.length > 0) {
      entry.emails = card.emails.map((e) => {
        const isPrimary = primaryEmail != null && e.value === primaryEmail
        return isPrimary ? { value: e.value, primary: true } : { value: e.value }
      })
    }
    if (card.phones.length > 0) {
      entry.phones = card.phones.map((p) => {
        const isPrimary = primaryPhone != null && p.value === primaryPhone
        const out: { value: string; type?: string; ext?: string; primary?: boolean } = { value: p.value }
        if (p.type) out.type = p.type
        if (p.ext) out.ext = p.ext
        if (isPrimary) out.primary = true
        return out
      })
    }
    return entry
  })

  // Exactly ONE primary flag per kind across the whole persons[] payload.
  let seenPrimaryEmail = false
  for (const p of persons) for (const e of p.emails || []) {
    if (e.primary) { if (seenPrimaryEmail) delete e.primary; else seenPrimaryEmail = true }
  }
  let seenPrimaryPhone = false
  for (const p of persons) for (const ph of p.phones || []) {
    if (ph.primary) { if (seenPrimaryPhone) delete ph.primary; else seenPrimaryPhone = true }
  }

  return {
    clientType: orgName ? 'organization' : 'person',
    // Couple displays composed by the name validation ("Alan & Karen Dorman")
    // pass through verbatim — never re-derived here. The person-type record is
    // named after the MAIN card (cp-create-main-v1); an unnamed main card
    // falls back to card 0's name (the pre-mainIndex behavior).
    recordName: orgName || mainCard.name || card0.name,
    primaryEmail,
    primaryPhone,
    card1InPersons,
    persons,
  }
}
